'use es6';

import { List, Map as ImmutableMap } from 'immutable';
import PropTypes from 'prop-types';
import { useContext, useMemo, useEffect } from 'react';
import { SEARCH } from 'reporting-data/constants/configTypes';
import { DEFAULT_FISCAL_YEAR_MONTH_NAME } from 'reporting-data/constants/dates';
import { fromMetricKey, METRIC_DELIMITER } from 'reporting-data/v2/dataset/datasetMetrics';
import { Dataset, getPropertyLabel, getReferenceLabel } from 'reporting-data/v2/dataset/datasetRecords';
import { DATAWELL, FUNNEL, PIVOT, SLICED, TABLE, XY, GAUGE } from 'reporting-visualizations/constants/visualizationTypes';
import Visualization, { VISUALIZATIONS } from 'reporting-visualizations/Visualization';
import { FiscalYearContext } from '../../../components/PlatformReport/FiscalYearProvider';
import { inferVisualization, getColumnOptions } from '../inferVisualization';
import { useFetchSingleObjectBrandKitColorPalettes } from 'reporting-ui-components/reports/hooks/color/useFetchSingleObjectBrandKitColorPalettes';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { THEME } from '../../../constants/themes';
import { isMultiConfigReport } from 'reporting-data/tsTypes/reportTypes';
import { getConfigByName } from 'reporting-data/report/multiConfigReportGetters';
import { UnifiedVizValidationContext } from 'reporting-ui-components/components/PlatformReport/v2/UnifiedVizValidationContextProvider';
import { Flex } from 'foundations-components/components/Flex';
import { useReportingThemes } from 'reporting-visualizations/hooks/useReportingThemes';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const fieldIsDrillableMeasure = (field, report, configKey) => {
  if (report.getIn(['config', 'configType']) === SEARCH) {
    return false;
  }
  let config = report.get('config');
  if (configKey && isMultiConfigReport(report)) {
    config = getConfigByName(report, configKey);
  }
  return config.get('metrics').some(metric => metric.get('property') === field);
};
const hasSomeDrilldownFunction = report => {
  const displayParams = report.get('displayParams') || ImmutableMap();
  return displayParams.get('columns', List()).some(column => column.get('onDrilldown')) || displayParams.get('onChartClick') || displayParams.get('onClick');
};
const getDrilldownFunction = ({
  report,
  clickEvent
}) => {
  const field = clickEvent.field || clickEvent.column && clickEvent.column.get('field') || clickEvent.cell && clickEvent.cell.get('field');
  const displayParams = report.get('displayParams') || ImmutableMap();
  return displayParams.getIn(['columns', fromMetricKey(field).property, 'onDrilldown']) || displayParams.get('onChartClick') || displayParams.get('onClick');
};
const TRANSLATE_CLICK_EVENT_BY_TYPE = {
  [FUNNEL]: eventInfo => {
    // Column funnel which leverages the XY viz drilldown
    if (eventInfo.layer && eventInfo.point) {
      const {
        layer,
        point
      } = eventInfo;
      const dim = layer.getIn(['x', 'field']);
      const seriesDim = layer.getIn(['color', 'field']);
      const metric = layer.getIn(['y', 'field']);
      const {
        property,
        type
      } = fromMetricKey(metric);
      const row = point.get('row');
      return {
        dim,
        seriesDim,
        metric,
        property,
        type,
        row
      };
    }

    // Vertical UIComponents driven funnel drilldown
    const {
      visualization,
      segment,
      row,
      field
    } = eventInfo;
    const metric = field || visualization.get('valueProperty');
    const {
      property,
      type
    } = fromMetricKey(metric);
    return {
      dim: visualization.get('filterProperty'),
      seriesDim: segment,
      metric,
      property,
      type,
      row
    };
  },
  [XY]: ({
    layer,
    point
  }) => {
    const dim = layer.getIn(['x', 'field']);
    const seriesDim = layer.getIn(['color', 'field']);
    const metric = layer.getIn(['y', 'field']);
    const {
      property,
      type
    } = fromMetricKey(metric);
    const row = point.get('row');
    const datasetKey = layer.getIn(['dataset', 'key']);
    return {
      dim,
      seriesDim,
      metric,
      property,
      type,
      row,
      datasetKey
    };
  },
  [SLICED]: ({
    layer,
    point
  }) => {
    const dim = layer.getIn(['color', 'field']);
    const seriesDim = layer.getIn(['color', 'field']);
    const metric = layer.getIn(['angle', 'field']);
    const {
      property,
      type
    } = fromMetricKey(metric);
    const row = point.get('sliceRow');
    return {
      dim,
      seriesDim,
      metric,
      property,
      type,
      row
    };
  },
  [DATAWELL]: ({
    cell,
    layer
  }, report) => {
    const isMultiConfig = isMultiConfigReport(report);
    const datasetKey = cell.get('dataset');
    let reportConfig = report.get('config');
    if (isMultiConfig) {
      reportConfig = getConfigByName(report, datasetKey);
    }
    const dim = isMultiConfig ? reportConfig.getIn(['dimensions', 0]) : layer.get('group');
    const metric = cell.get('field');
    const {
      property,
      type
    } = fromMetricKey(metric);
    return {
      dim,
      metric,
      property,
      type,
      row: cell.get('row'),
      datasetKey
    };
  },
  [TABLE]: (clickEvent, report) => {
    const isMultiConfig = isMultiConfigReport(report);
    const {
      row,
      field: metric,
      column
    } = clickEvent;
    const datasetKey = column === null || column === void 0 ? void 0 : column.getIn(['dataset', 'key']);
    let reportConfig = report.get('config');
    const [...keys] = row.keys();
    let dim = report.getIn(['config', 'dimensions', 0]) || keys.find(key => !key.includes(METRIC_DELIMITER));
    if (isMultiConfig) {
      reportConfig = getConfigByName(report, datasetKey);
      dim = reportConfig.getIn(['dimensions', 0]);
    }
    const {
      property,
      type
    } = fromMetricKey(metric);
    return {
      dim,
      metric,
      property,
      type,
      row,
      datasetKey
    };
  },
  [PIVOT]: ({
    row,
    field,
    pivot
  }) => {
    const metric = field;
    const dim = pivot.getIn(['groupby', 0]);
    const seriesDim = pivot.get('field');
    const {
      property,
      type
    } = fromMetricKey(metric);
    return {
      dim,
      metric: dim,
      property,
      seriesDim,
      type,
      row
    };
  },
  [GAUGE]: ({
    layer,
    row
  }) => {
    const dim = undefined;
    const metric = layer.get('dataColumn');
    const {
      property,
      type
    } = fromMetricKey(metric);
    const datasetKey = layer.get('dataset');
    return {
      dim,
      metric,
      property,
      type,
      row,
      datasetKey
    };
  }
};
export const translateClickEvent = (report, visualizationType, clickEvent) => {
  const onDrilldown = getDrilldownFunction({
    report,
    clickEvent
  });
  if (!onDrilldown) {
    return;
  }
  const translateFn = TRANSLATE_CLICK_EVENT_BY_TYPE[visualizationType];
  if (!translateFn) {
    console.warn(`Unable to translate click event to drilldown for ${visualizationType} `);
    return;
  }
  const {
    dataset
  } = clickEvent;
  const {
    dim,
    seriesDim,
    metric,
    property,
    type,
    row,
    datasetKey
  } = translateFn(clickEvent, report);
  let drilldownName;
  if (dataset) {
    const metricLabel = visualizationType === PIVOT ? getReferenceLabel(dataset, seriesDim, row.get(seriesDim)) : getPropertyLabel(dataset, metric);
    drilldownName = dim ? `${metricLabel} / ${getReferenceLabel(dataset, dim, row.get(dim))}` : `${metricLabel}`;
  }
  const drilldownList = List([...(dim ? [row.get(dim)] : []),
  // Primary dimension value (e.g., "NEW")
  ...(seriesDim && seriesDim !== dim ? [row.get(seriesDim)] : []),
  // Series dimension value - skipped when present for custom colors only and not for a second dimension
  property,
  // Metric property name (e.g., "count") - generators use keys.get(dimensions.count()) to find this
  type // Aggregation type (e.g., "SUM")
  ]);
  onDrilldown(drilldownList, Object.assign({}, drilldownName && {
    name: drilldownName
  }, {
    datasetKey
  }));
};
const setupClickEventTranslator = (report, visualizationType) => hasSomeDrilldownFunction(report) ? clickEvent => translateClickEvent(report, visualizationType, clickEvent) : undefined;
export const defaultDetermineVisualization = (report, dataMap, isMobile, fiscalYearMonthName = DEFAULT_FISCAL_YEAR_MONTH_NAME, reportingThemes, brandKitPalettes, compact, mobileReportingOptions, disableZoom) => {
  const visualization = inferVisualization(report, dataMap, isMobile, fiscalYearMonthName, reportingThemes, brandKitPalettes, compact, mobileReportingOptions, disableZoom);
  const visualizationType = visualization.get('type');
  if (visualizationType === XY) {
    return visualization.set('events', {
      onPointClick: report.getIn(['displayParams', 'allowDrilldown'], true) ? setupClickEventTranslator(report, XY) : undefined
    });
  }
  if (visualizationType === SLICED) {
    return visualization.set('events', {
      onPointClick: setupClickEventTranslator(report, SLICED)
    });
  }
  if (visualizationType === FUNNEL) {
    return visualization.set('events', {
      onPointClick: setupClickEventTranslator(report, FUNNEL)
    });
  }
  if ([DATAWELL, TABLE, PIVOT].includes(visualizationType)) {
    const onPointClick = clickEvent => {
      const field = String(clickEvent.column && clickEvent.column.get('field') || clickEvent.cell && clickEvent.cell.get('field'));
      let datasetKey = clickEvent.cell && clickEvent.cell.get('dataset');
      if (visualizationType === TABLE) {
        var _clickEvent$column;
        datasetKey = clickEvent === null || clickEvent === void 0 || (_clickEvent$column = clickEvent.column) === null || _clickEvent$column === void 0 || (_clickEvent$column = _clickEvent$column.get('dataset')) === null || _clickEvent$column === void 0 ? void 0 : _clickEvent$column.get('key');
      }
      const columnOptions = getColumnOptions(report, field);
      const allowColumnDrilldown = columnOptions === null || columnOptions === void 0 ? void 0 : columnOptions.get('allowDrilldown');
      const canDrilldown = field && allowColumnDrilldown !== false && (fieldIsDrillableMeasure(fromMetricKey(field).property, report, datasetKey) || visualizationType === 'PIVOT') && getDrilldownFunction({
        report,
        clickEvent
      });
      return canDrilldown ? (overrideClickEventArgs = {}) => translateClickEvent(report, visualizationType, Object.assign({}, clickEvent, overrideClickEventArgs)) : null;
    };
    if (visualizationType === DATAWELL) {
      return visualization.set('events', {
        getOnPointClick: onPointClick,
        getHeader: report.getIn(['displayParams', 'render', 'header']),
        getBody: report.getIn(['displayParams', 'render', 'body'])
      });
    }
    return visualization.set('events', {
      getOnPointClick: onPointClick
    });
  }
  return visualization;
};
const ReportWithVisualization = ({
  report,
  data,
  paginationRenderer,
  visualizationDebug,
  determineVisualization = defaultDetermineVisualization,
  isMobile = false,
  gates = [],
  compact,
  mobileReportingOptions,
  vizContainerStyleProps,
  disableZoom
}) => {
  const fiscalYearMonthName = useContext(FiscalYearContext);
  const {
    setLegacyVisualization
  } = useContext(UnifiedVizValidationContext);
  const shouldDeferBrandKitFetching = useMemo(() => report.getIn(['displayParams', 'colors', 'theme']) !== THEME.BRAND_KIT, [report]);
  const {
    isLoading: brandKitLoading,
    soBrandKitMap: brandKitPalettes
  } = useFetchSingleObjectBrandKitColorPalettes(shouldDeferBrandKitFetching);
  const reportingThemes = useReportingThemes();
  const visualization = useMemo(() => {
    const baseViz = determineVisualization(report, data, isMobile, fiscalYearMonthName, reportingThemes, brandKitPalettes, compact, mobileReportingOptions, disableZoom);
    if (report.has('visualization')) {
      const type = report.getIn(['visualization', 'type']);
      const VizRecord = VISUALIZATIONS[type].record;
      const customViz = VizRecord(ImmutableMap(baseViz).merge(report.get('visualization')));
      let dataMap = data.map(dataset => Dataset(dataset.get('data')));
      if (type === TABLE && !dataMap.has('summary')) {
        // Table visualization expect summary dataset regardless of value of displayParams.showTotal
        // MultiConfig resolve doesn't yet populate summary dataSet yet, adding an empty value if the
        // summary value doesn't exist.
        dataMap = dataMap.set('summary', Dataset());
      }
      return customViz.set('data', dataMap);
    }
    return baseViz;
  }, [determineVisualization, report, data, isMobile, fiscalYearMonthName, brandKitPalettes, compact, mobileReportingOptions, disableZoom, reportingThemes]);
  useEffect(() => {
    try {
      if (!brandKitLoading) {
        setLegacyVisualization(visualization);
      }
    } catch (e) {
      console.error('Error setting legacy visualization', e);

      // TODO: Log error with ReportingLogger
    }
  }, [setLegacyVisualization, visualization, brandKitLoading]);
  if (brandKitLoading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      grow: true
    });
  }
  const rhumbConfig = report.getIn(['displayParams', 'rhumbConfig'], ImmutableMap());
  const renderSuccessMarker = rhumbConfig.get('renderSuccessMarker');
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    flexGrow: 1,
    maxBlockSize: "100%",
    overflow: "auto",
    "data-status-id": "report-status-loaded",
    "data-test-id": "reporting-visualization-report",
    children: [/*#__PURE__*/_jsx(Visualization, {
      visualization: visualization,
      debug: visualizationDebug,
      gates: gates,
      vizContainerStyleProps: vizContainerStyleProps
    }), !report.getIn(['displayParams', 'hidePagination']) && paginationRenderer && paginationRenderer(), renderSuccessMarker && renderSuccessMarker()]
  });
};
ReportWithVisualization.propTypes = {
  report: PropTypes.object,
  data: PropTypes.object,
  paginationRenderer: PropTypes.func,
  determineVisualization: PropTypes.func,
  gates: PropTypes.array,
  isMobile: PropTypes.bool,
  compact: PropTypes.bool,
  disableZoom: PropTypes.bool,
  /* For debugging v2 visualizations */
  visualizationDebug: PropTypes.object
};
export const __TESTABLE__ = {
  translateClickEvent,
  fieldIsDrillableMeasure,
  TRANSLATE_CLICK_EVENT_BY_TYPE,
  defaultDetermineVisualization
};
export default ReportWithVisualization;